import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package, 
  Copy, 
  Eye, 
  DollarSign, 
  Users, 
  Target,
  CheckCircle,
  Clock,
  Loader2,
  Pause,
  Archive
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PackageSlot {
  slot_id: string;
  show_id: string;
  show_name: string;
  episode: string;
  slot_type: 'narrative_integration' | 'product_placement';
  rate: number;
  integration_context?: string;
}

interface PackageShow {
  // Support both new and legacy field names
  show_id?: string;
  project_id?: string;
  show_name?: string;
  project_name?: string;
  creator: string;
  vertical: string;
  artie_score: number;
  forecasted_reach_90d: number;
  hero_image_url?: string;
  is_anchor: boolean;
  slots?: PackageSlot[];
}

interface PackageTargeting {
  target_demographics: string[];
  target_categories: string[];
  min_budget: number;
  max_budget: number;
  target_ecpm_range: {
    min: number;
    max: number;
  };
}

interface Package {
  id: string;
  name: string;
  headline: string;
  description: string;
  brand_id?: string; // Brand this package is created for
  brand_name?: string; // Brand name (fetched from brand_id)
  brand_campaign_id?: string; // Campaign this package was created for (backward compatibility)
  // Support both new and legacy field names
  anchor_projects?: PackageShow[];
  fill_projects?: PackageShow[];
  anchor_shows?: PackageShow[]; // Legacy field
  fill_shows?: PackageShow[]; // Legacy field
  targeting: PackageTargeting;
  total_cost: number;
  total_reach: number;
  total_slots: number;
  fit_rationale: string;
  suitability_compliance: string[];
  status: 'draft' | 'active' | 'paused' | 'archived' | 'rejected' | 'hold_placed';
  held_by_campaign_id?: string | null; // Campaign ID that has placed a hold on this package
  hold_expires_at?: string | null; // When the hold expires
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface ReachMetrics {
  total_followers: number;
  total_views: number;
  total_subscribers: number;
  platform_breakdown: {
    tiktok: { followers: number; views: number };
    instagram: { followers: number; views: number };
    youtube: { subscribers: number; views: number };
    imdb: { rating: number; reviews_count: number };
  };
  projects_with_metrics: number;
  total_projects: number;
}

interface ReachMetricsResponse {
  package_id: string;
  package_name: string;
  reach_metrics: ReachMetrics;
  anchor_projects?: PackageShow[];
  fill_projects?: PackageShow[];
}

interface AvailableShow {
  id: string;
  show_name: string;
  creator: string;
  vertical: string;
  artie_score: number;
  forecasted_reach_90d: number;
  hero_image_url?: string;
  has_brand_assignments?: boolean;
  brand_assignment_count?: number;
  total_slots?: number;
  platform_metrics?: {
    total_followers: number;
    total_views: number;
    total_subscribers: number;
    platform_breakdown: {
      tiktok: { followers: number; views: number };
      instagram: { followers: number; views: number };
      youtube: { subscribers: number; views: number };
      imdb: { rating: number; reviews_count: number };
    };
  };
  available_slots: {
    id: string;
    episode: string;
    slot_type: string;
    rate: number;
    integration_context?: string;
    status?: string;
  }[];
  assigned_slots?: {
    id: string;
    episode: string;
    slot_type: string;
    rate: number;
    integration_context?: string;
    status?: string;
    assigned_brand_campaign_id?: string;
  }[];
}

type PackageFormErrors = {
  name?: string;
  brand_id?: string;
  campaign_id?: string;
  min_budget?: string;
  max_budget?: string;
  target_ecpm_min?: string;
  target_ecpm_max?: string;
  anchor?: string;
};

const PackageManagementPage: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [pendingPackages, setPendingPackages] = useState<Package[]>([]);
  const [availableShows, setAvailableShows] = useState<AvailableShow[]>([]);
  const [brands, setBrands] = useState<{id: string, name: string}[]>([]);
  const [campaigns, setCampaigns] = useState<{id: string, name: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingShows, setIsLoadingShows] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isRefreshingPackages, setIsRefreshingPackages] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);
  const [duplicatingPackageId, setDuplicatingPackageId] = useState<string | null>(null);
  const [approvingPackageId, setApprovingPackageId] = useState<string | null>(null);
  const [rejectingPackageId, setRejectingPackageId] = useState<string | null>(null);
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingPackage, setViewingPackage] = useState<Package | null>(null);
  const [viewingPackageReachMetrics, setViewingPackageReachMetrics] = useState<ReachMetricsResponse | null>(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    headline: '',
    description: '',
    fit_rationale: '',
    brand_id: '',
    campaign_id: '',
    min_budget: 0,
    max_budget: 0,
    target_demographics: [] as string[],
    target_categories: [] as string[],
    target_ecpm_min: 0,
    target_ecpm_max: 0,
    suitability_compliance: [] as string[]
  });
  const [selectedShows, setSelectedShows] = useState<{
    anchor: AvailableShow[];
    fill: AvailableShow[];
  }>({
    anchor: [],
    fill: []
  });
  const [packageFormErrors, setPackageFormErrors] = useState<PackageFormErrors>({});
  const { toast } = useToast();

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    active: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800',
    archived: 'bg-red-100 text-red-800',
    rejected: 'bg-red-200 text-red-900',
    hold_placed: 'bg-orange-100 text-orange-800'
  };

  const statusIcons = {
    draft: <Edit className="h-4 w-4" />,
    active: <CheckCircle className="h-4 w-4" />,
    paused: <Pause className="h-4 w-4" />,
    archived: <Archive className="h-4 w-4" />,
    rejected: <Trash2 className="h-4 w-4" />,
    hold_placed: <Clock className="h-4 w-4" />
  };

  const fetchPackages = async () => {
    try {
      const data = await apiClient.get<{ packages: any[] }>('/api/v1/ops-admin/packages');
      console.log('DEBUG: Fetched packages data:', data.packages);
      
      // Debug each package's fill_shows data and hold status
      data.packages?.forEach((pkg: any, index: number) => {
        console.log(`DEBUG: Package ${index + 1} (${pkg.name}):`);
        console.log(`  fill_shows:`, pkg.fill_shows);
        console.log(`  fill_projects:`, pkg.fill_projects);
        console.log(`  anchor_shows:`, pkg.anchor_shows);
        console.log(`  anchor_projects:`, pkg.anchor_projects);
        console.log(`  held_by_campaign_id:`, pkg.held_by_campaign_id);
        console.log(`  campaign_status:`, pkg.campaign_status);
        console.log(`  campaign_name:`, pkg.campaign_name);
        console.log(`  is_on_hold:`, (pkg.held_by_campaign_id != null || pkg.campaign_status === 'hold'));
      });
      
      setPackages(data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast({
        title: "Error",
        description: "Failed to fetch packages",
        variant: "destructive"
      });
    }
  };

  const fetchAvailableShows = async (brandId?: string) => {
    setIsLoadingShows(true);
    try {
      const params = new URLSearchParams();
      if (brandId) {
        params.append('brand_id', brandId);
      }
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${API_BASE_URL}/api/v1/ops-admin/package-builder/available-shows${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('DEBUG: Available shows data:', data.shows);
        
        // Debug each show's available_slots, assigned_slots, and platform_metrics
        data.shows?.forEach((show: any, index: number) => {
          console.log(`DEBUG: Available show ${index + 1} (${show.show_name}):`);
          console.log(`  available_slots:`, show.available_slots);
          console.log(`  available_slots length:`, show.available_slots?.length || 0);
          console.log(`  assigned_slots:`, show.assigned_slots);
          console.log(`  assigned_slots length:`, show.assigned_slots?.length || 0);
          console.log(`  total slots:`, (show.available_slots?.length || 0) + (show.assigned_slots?.length || 0));
          console.log(`  platform_metrics:`, show.platform_metrics);
          console.log(`  has_platform_metrics:`, !!show.platform_metrics);
          if (show.platform_metrics) {
            console.log(`    total_followers:`, show.platform_metrics.total_followers);
            console.log(`    total_views:`, show.platform_metrics.total_views);
            console.log(`    total_subscribers:`, show.platform_metrics.total_subscribers);
            console.log(`    platform_breakdown:`, show.platform_metrics.platform_breakdown);
          }
        });
        
        setAvailableShows(data.shows || []);
      } else {
        console.error('Error fetching available shows:', response.status);
        setAvailableShows([]);
        toast({
          title: "Error",
          description: "Failed to load available shows for the selected brand.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching available shows:', error);
      setAvailableShows([]);
    } finally {
      setIsLoadingShows(false);
    }
  };

  const fetchPendingPackages = async () => {
    try {
      const data = await apiClient.get<{ packages: any[] }>('/api/v1/ops-admin/packages/pending');
      setPendingPackages(data.packages || []);
    } catch (error) {
      console.error('Error fetching pending packages:', error);
      toast({
        title: "Error",
        description: "Failed to fetch pending packages",
        variant: "destructive"
      });
    }
  };

  const refreshPackageLists = async () => {
    setIsRefreshingPackages(true);
    try {
      await Promise.all([fetchPackages(), fetchPendingPackages()]);
    } finally {
      setIsRefreshingPackages(false);
    }
  };

  const handleApprovePackage = async (packageId: string) => {
    if (approvingPackageId) return;
    setApprovingPackageId(packageId);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to approve package');
      }

      toast({
        title: "Success",
        description: "Package approved successfully"
      });
      // Refresh both lists
      await refreshPackageLists();
    } catch (error) {
      console.error('Error approving package:', error);
      toast({
        title: "Error",
        description: "Failed to approve package",
        variant: "destructive"
      });
    } finally {
      setApprovingPackageId(null);
    }
  };

  const handleRejectPackage = async (packageId: string, reason?: string) => {
    if (rejectingPackageId) return;
    setRejectingPackageId(packageId);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const url = new URL(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}/reject`, window.location.origin);
      if (reason) {
        url.searchParams.append('rejection_reason', reason);
      }
      
      const response = await fetch(url.toString(), {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Package rejected successfully"
        });
        // Refresh both lists
        await refreshPackageLists();
      } else {
        throw new Error('Failed to reject package');
      }
    } catch (error) {
      console.error('Error rejecting package:', error);
      toast({
        title: "Error",
        description: "Failed to reject package",
        variant: "destructive"
      });
    } finally {
      setRejectingPackageId(null);
    }
  };

  const handleCreatePackage = async () => {
    if (isCreatingPackage) return;
    setIsCreatingPackage(true);
    const errors: PackageFormErrors = {};
    if (!packageForm.name.trim()) errors.name = 'Package name is required.';
    if (!packageForm.brand_id) errors.brand_id = 'Brand is required.';
    if (!packageForm.campaign_id) errors.campaign_id = 'Campaign is required.';
    if (packageForm.min_budget < 0) errors.min_budget = 'Min budget cannot be negative.';
    if (packageForm.max_budget < 0) errors.max_budget = 'Max budget cannot be negative.';
    if (packageForm.max_budget > 0 && packageForm.min_budget > packageForm.max_budget) {
      errors.max_budget = 'Max budget must be greater than or equal to min budget.';
    }
    if (packageForm.target_ecpm_min < 0) errors.target_ecpm_min = 'Min eCPM cannot be negative.';
    if (packageForm.target_ecpm_max < 0) errors.target_ecpm_max = 'Max eCPM cannot be negative.';
    if (packageForm.target_ecpm_max > 0 && packageForm.target_ecpm_min > packageForm.target_ecpm_max) {
      errors.target_ecpm_max = 'Max eCPM must be greater than or equal to min eCPM.';
    }
    if (selectedShows.anchor.length === 0) errors.anchor = 'Select at least one anchor show.';

    if (Object.keys(errors).length > 0) {
      setPackageFormErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the package form fields.",
        variant: "destructive"
      });
      setIsCreatingPackage(false);
      return;
    }

    // Validation passed; clear any prior field errors.
    setPackageFormErrors({});

    try {
      // Build package data from form and selected shows
      const packageData = {
        name: packageForm.name,
        headline: packageForm.headline,
        description: packageForm.description,
        fit_rationale: packageForm.fit_rationale,
        brand_id: packageForm.brand_id,
        campaign_id: packageForm.campaign_id,
        anchor_shows: selectedShows.anchor.map(show => ({
          show_id: show.id,
          show_name: show.show_name,
          creator: show.creator,
          vertical: show.vertical,
          artie_score: show.artie_score,
          forecasted_reach_90d: show.forecasted_reach_90d,
          hero_image_url: show.hero_image_url,
          is_anchor: true,
          slots: [...(show.available_slots || []), ...(show.assigned_slots || [])].map(slot => ({
            slot_id: slot.id,
            show_id: show.id,
            show_name: show.show_name,
            episode: slot.episode,
            slot_type: slot.slot_type as 'narrative_integration' | 'product_placement',
            rate: slot.rate,
            integration_context: slot.integration_context,
            status: slot.status || 'available'
          }))
        })),
        fill_shows: selectedShows.fill.map(show => ({
          show_id: show.id,
          show_name: show.show_name,
          creator: show.creator,
          vertical: show.vertical,
          artie_score: show.artie_score,
          forecasted_reach_90d: show.forecasted_reach_90d,
          hero_image_url: show.hero_image_url,
          is_anchor: false,
          slots: [...(show.available_slots || []), ...(show.assigned_slots || [])].map(slot => ({
            slot_id: slot.id,
            show_id: show.id,
            show_name: show.show_name,
            episode: slot.episode,
            slot_type: slot.slot_type as 'narrative_integration' | 'product_placement',
            rate: slot.rate,
            integration_context: slot.integration_context,
            status: slot.status || 'available'
          }))
        })),
        targeting: {
          target_demographics: packageForm.target_demographics,
          target_categories: packageForm.target_categories,
          min_budget: packageForm.min_budget,
          max_budget: packageForm.max_budget,
          target_ecpm_range: {
            min: packageForm.target_ecpm_min,
            max: packageForm.target_ecpm_max
          }
        },
        total_cost: [...selectedShows.anchor, ...selectedShows.fill].reduce((sum, show) =>
          sum + [...(show.available_slots || []), ...(show.assigned_slots || [])].reduce((slotSum, slot) => slotSum + slot.rate, 0), 0
        ),
        total_reach: [...selectedShows.anchor, ...selectedShows.fill].reduce((sum, show) => {
          // Use platform metrics like in package details view
          const platformMetrics = (show as any).platform_metrics;
          if (platformMetrics) {
            return sum + (platformMetrics.total_followers || 0) + (platformMetrics.total_views || 0) + (platformMetrics.total_subscribers || 0);
          }
          return sum;
        }, 0),
        total_slots: [...selectedShows.anchor, ...selectedShows.fill].reduce((sum, show) =>
          sum + [...(show.available_slots || []), ...(show.assigned_slots || [])].length, 0
        ),
        suitability_compliance: packageForm.suitability_compliance,
        status: 'draft'
      };

      // Debug: Log the package data being sent
      console.log('DEBUG: Package data being sent to backend:', packageData);
      console.log('DEBUG: selectedShows.fill:', selectedShows.fill);
      console.log('DEBUG: packageData.fill_shows:', packageData.fill_shows);

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // This ensures cookies are sent
        body: JSON.stringify(packageData)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Package created successfully"
        });
        setIsCreateDialogOpen(false);
        resetForm();
        // Refresh both package lists since new packages are created with "draft" status
        await refreshPackageLists();
      } else {
        throw new Error('Failed to create package');
      }
    } catch (error) {
      console.error('Error creating package:', error);
      toast({
        title: "Error",
        description: "Failed to create package",
        variant: "destructive"
      });
    } finally {
      setIsCreatingPackage(false);
    }
  };

  const handleUpdatePackageStatus = async (packageId: string, status: string) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Package status updated successfully"
        });
        fetchPackages();
      }
    } catch (error) {
      console.error('Error updating package status:', error);
      toast({
        title: "Error",
        description: "Failed to update package status",
        variant: "destructive"
      });
    }
  };

  const handleDuplicatePackage = async (packageId: string) => {
    if (duplicatingPackageId) return;
    setDuplicatingPackageId(packageId);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}/duplicate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        let detail = response.statusText;
        try {
          const text = await response.text();
          if (text) detail = text;
        } catch {
          // Ignore body parsing errors.
        }
        throw new Error(`Failed to duplicate (${response.status}): ${detail}`);
      }

      toast({
        title: "Success",
        description: "Package duplicated successfully"
      });
      await refreshPackageLists();
    } catch (error) {
      console.error('Error duplicating package:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to duplicate package",
        variant: "destructive"
      });
    } finally {
      setDuplicatingPackageId(null);
    }
  };

  const handleCopyPackageRow = async (pkg: Package) => {
    const anchorCount = ((pkg.anchor_projects && pkg.anchor_projects.length > 0) ? pkg.anchor_projects : pkg.anchor_shows || []).length;
    const fillCount = ((pkg.fill_projects && pkg.fill_projects.length > 0) ? pkg.fill_projects : pkg.fill_shows || []).length;

    const rowPayload = {
      id: pkg.id,
      name: pkg.name,
      headline: pkg.headline,
      brand_id: pkg.brand_id ?? null,
      brand_name: pkg.brand_name ?? null,
      campaign_id: (pkg as any).campaign_id ?? pkg.brand_campaign_id ?? null,
      status: pkg.status,
      created_at: pkg.created_at,
      updated_at: pkg.updated_at,
      anchor_count: anchorCount,
      fill_count: fillCount,
      total_cost: pkg.total_cost,
      total_reach: pkg.total_reach,
      total_slots: pkg.total_slots,
    };

    const text = JSON.stringify(rowPayload, null, 2);

    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Package row info copied to clipboard.",
      });
    } catch (e) {
      // Fallback for environments where `navigator.clipboard` is not permitted.
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        toast({
          title: "Copied",
          description: "Package row info copied to clipboard.",
        });
      } catch (fallbackError) {
        console.error('Error copying package row:', fallbackError);
        toast({
          title: "Copy failed",
          description: "Could not access clipboard.",
          variant: "destructive"
        });
      }
    }
  };

  const handleViewPackage = async (pkg: Package) => {
    console.log('DEBUG: Viewing package:', pkg);
    console.log('DEBUG: Package fill_shows:', pkg.fill_shows);
    console.log('DEBUG: Package fill_projects:', pkg.fill_projects);
    console.log('DEBUG: Package anchor_shows:', pkg.anchor_shows);
    console.log('DEBUG: Package anchor_projects:', pkg.anchor_projects);
    
    setViewingPackage(pkg);
    setIsViewDialogOpen(true);
    
    // Fetch reach metrics for this package
    const reachMetricsResponse = await fetchPackageReachMetrics(pkg.id);
    setViewingPackageReachMetrics(reachMetricsResponse);
    
    // Update the viewing package with enriched project data if available
    if (reachMetricsResponse && (reachMetricsResponse.anchor_projects || reachMetricsResponse.fill_projects)) {
      const enrichedPackage = {
        ...pkg,
        anchor_projects: reachMetricsResponse.anchor_projects || pkg.anchor_projects || pkg.anchor_shows,
        fill_projects: reachMetricsResponse.fill_projects || pkg.fill_projects || pkg.fill_shows
      };
      setViewingPackage(enrichedPackage);
      console.log('DEBUG: Updated viewing package with enriched project data');
    }
  };

  const handleDeletePackage = async (packageId: string, packageName: string, packageStatus?: string) => {
    // Check if package is hold-placed and prevent deletion
    if (packageStatus === 'hold_placed' || packageStatus === 'hold-placed') {
      toast({
        title: "Cannot Delete Package",
        description: "This package has a hold placed and cannot be deleted. Please remove the hold first or wait for it to expire.",
        variant: "destructive"
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete the package "${packageName}"? This action cannot be undone.`)) {
      return;
    }

    if (deletingPackageId) return;
    setDeletingPackageId(packageId);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Package deleted successfully"
        });
        // Refresh both lists
        await refreshPackageLists();
      } else {
        throw new Error('Failed to delete package');
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      toast({
        title: "Error",
        description: "Failed to delete package",
        variant: "destructive"
      });
    } finally {
      setDeletingPackageId(null);
    }
  };

  const resetForm = () => {
    setPackageForm({
      name: '',
      headline: '',
      description: '',
      fit_rationale: '',
      brand_id: '',
      campaign_id: '',
      min_budget: 0,
      max_budget: 0,
      target_demographics: [],
      target_categories: [],
      target_ecpm_min: 0,
      target_ecpm_max: 0,
      suitability_compliance: []
    });
    setAvailableShows([]);
    setCampaigns([]);
    setSelectedShows({ anchor: [], fill: [] });
    setPackageFormErrors({});
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const fetchBrands = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/brands`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands?.map((brand: any) => ({
          id: brand.id || brand._id,
          name: brand.name || brand.advertiser || 'Unknown Brand'
        })) || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchCampaigns = async (brandId?: string) => {
    setIsLoadingCampaigns(true);
    try {
      const params = new URLSearchParams();
      if (brandId) {
        params.append('brand_id', brandId);
      }
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${API_BASE_URL}/api/v1/ops-admin/campaigns${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns?.map((campaign: any) => ({
          id: campaign.id,
          name: campaign.name || 'Unknown Campaign'
        })) || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const fetchPackageReachMetrics = async (packageId: string): Promise<ReachMetricsResponse | null> => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-admin/packages/${packageId}/reach-metrics`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error(`Failed to fetch reach metrics: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching package reach metrics:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPackages(), fetchPendingPackages(), fetchBrands()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Keep brand-dependent data in sync with the selected brand.
  useEffect(() => {
    if (!packageForm.brand_id) {
      setCampaigns([]);
      setAvailableShows([]);
      setIsLoadingShows(false);
      setIsLoadingCampaigns(false);
      return;
    }
    // Brand changed: clear stale inventory immediately, then refetch.
    setAvailableShows([]);
    setSelectedShows({ anchor: [], fill: [] });
    setPackageFormErrors(prev => {
      if (!prev.anchor) return prev;
      return { ...prev, anchor: undefined };
    });
    fetchAvailableShows(packageForm.brand_id);
    fetchCampaigns(packageForm.brand_id);
  }, [packageForm.brand_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Package Management</h1>
          <p className="text-gray-600">Create and manage curated packages for the brand marketplace</p>
        </div>
        <Dialog
          open={isCreateDialogOpen}
          onOpenChange={(open) => {
            setIsCreateDialogOpen(open);
            if (!open) {
              setIsCreatingPackage(false);
              resetForm();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Package</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Package Name *</Label>
                    <Input
                      id="name"
                      value={packageForm.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPackageForm(prev => ({ ...prev, name: value }));
                        setPackageFormErrors(prev => {
                          if (!prev.name) return prev;
                          const next = { ...prev };
                          delete next.name;
                          return next;
                        });
                      }}
                      placeholder="e.g., Premium Comedy Package Q1 2024"
                      required
                    />
                    {packageFormErrors.name && <p className="text-xs text-red-600 mt-1">{packageFormErrors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="headline">Marketing Headline</Label>
                    <Input
                      id="headline"
                      value={packageForm.headline}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="e.g., High-Engagement Comedy Shows"
                    />
                  </div>
                  <div>
                    <Label htmlFor="brand">Target Brand *</Label>
                    <select
                      id="brand"
                      value={packageForm.brand_id}
                      onChange={(e) => {
                        const brandId = e.target.value;
                        setPackageForm(prev => ({ ...prev, brand_id: brandId, campaign_id: '' }));
                        setPackageFormErrors(prev => {
                          if (!prev.brand_id && !prev.campaign_id) return prev;
                          const next = { ...prev };
                          delete next.brand_id;
                          delete next.campaign_id;
                          return next;
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a brand</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                    {packageFormErrors.brand_id && <p className="text-xs text-red-600 mt-1">{packageFormErrors.brand_id}</p>}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="campaign">Target Campaign *</Label>
                      {isLoadingCampaigns && (
                        <span className="inline-flex items-center text-xs text-gray-500">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Fetching campaigns...
                        </span>
                      )}
                    </div>
                    <select
                      id="campaign"
                      value={packageForm.campaign_id}
                      onChange={(e) => {
                        const campaignId = e.target.value;
                        setPackageForm(prev => ({ ...prev, campaign_id: campaignId }));
                        setPackageFormErrors(prev => {
                          if (!prev.campaign_id) return prev;
                          const next = { ...prev };
                          delete next.campaign_id;
                          return next;
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!packageForm.brand_id}
                    >
                      <option value="">Select a campaign</option>
                      {campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                    {packageFormErrors.campaign_id && <p className="text-xs text-red-600 mt-1">{packageFormErrors.campaign_id}</p>}
                    {!packageForm.brand_id && (
                      <p className="text-sm text-gray-500 mt-1">Select a brand first to see campaigns</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={packageForm.description}
                    onChange={(e) => setPackageForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Detailed description of the package..."
                  />
                </div>
                <div>
                  <Label htmlFor="fit_rationale">Fit Rationale</Label>
                  <Textarea
                    id="fit_rationale"
                    value={packageForm.fit_rationale}
                    onChange={(e) => setPackageForm(prev => ({ ...prev, fit_rationale: e.target.value }))}
                    rows={3}
                    placeholder="Why this package works for brands..."
                  />
                </div>
              </div>

              {/* Targeting */}
              <div className="space-y-4">
                <h3 className="font-medium">Targeting & Budget</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min_budget">Min Budget ($)</Label>
                    <Input
                      id="min_budget"
                      type="number"
                      value={packageForm.min_budget}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, min_budget: Number(e.target.value) }))}
                    />
                    {packageFormErrors.min_budget && <p className="text-xs text-red-600 mt-1">{packageFormErrors.min_budget}</p>}
                  </div>
                  <div>
                    <Label htmlFor="max_budget">Max Budget ($)</Label>
                    <Input
                      id="max_budget"
                      type="number"
                      value={packageForm.max_budget}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, max_budget: Number(e.target.value) }))}
                    />
                    {packageFormErrors.max_budget && <p className="text-xs text-red-600 mt-1">{packageFormErrors.max_budget}</p>}
                  </div>
                  <div>
                    <Label htmlFor="target_ecpm_min">Min eCPM ($)</Label>
                    <Input
                      id="target_ecpm_min"
                      type="number"
                      step="0.01"
                      value={packageForm.target_ecpm_min}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, target_ecpm_min: Number(e.target.value) }))}
                      placeholder="e.g., 5.00"
                    />
                    {packageFormErrors.target_ecpm_min && <p className="text-xs text-red-600 mt-1">{packageFormErrors.target_ecpm_min}</p>}
                  </div>
                  <div>
                    <Label htmlFor="target_ecpm_max">Max eCPM ($)</Label>
                    <Input
                      id="target_ecpm_max"
                      type="number"
                      step="0.01"
                      value={packageForm.target_ecpm_max}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, target_ecpm_max: Number(e.target.value) }))}
                      placeholder="e.g., 15.00"
                    />
                    {packageFormErrors.target_ecpm_max && <p className="text-xs text-red-600 mt-1">{packageFormErrors.target_ecpm_max}</p>}
                  </div>
                </div>
              </div>

              {/* Show Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">Show Selection</h3>
                  {isLoadingShows && (
                    <span className="inline-flex items-center text-xs text-gray-500">
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Fetching shows...
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  Select shows from available inventory. Anchor shows are primary placements, fill shows extend reach.
                  {packageForm.brand_id && (
                    <span className="block mt-1 text-blue-600 font-medium">
                      ⭐ Shows with existing assignments for this brand are prioritized and highlighted.
                    </span>
                  )}
                </p>
                {packageFormErrors.anchor && <p className="text-xs text-red-600">{packageFormErrors.anchor}</p>}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">Available Shows</h4>
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      {availableShows.map((show) => (
                        <div key={show.id} className={`p-3 border-b last:border-b-0 hover:bg-gray-50 ${show.has_brand_assignments ? 'bg-blue-25 border-l-4 border-l-blue-400' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h5 className="font-medium text-sm">{show.show_name}</h5>
                                {show.has_brand_assignments && (
                                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                                    ⭐ Brand Match ({show.brand_assignment_count})
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-600">{show.creator}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">{show.vertical}</Badge>
                                <span className="text-xs">Score: {show.artie_score.toFixed(1)}</span>
                                <span className="text-xs">{show.total_slots || ((show.available_slots?.length || 0) + (show.assigned_slots?.length || 0))} slots</span>
                                {show.has_brand_assignments && (
                                  <span className="text-xs text-blue-600 font-medium">
                                    {show.brand_assignment_count} existing assignment{show.brand_assignment_count !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              
                              {/* Platform Metrics Display */}
                              {show.platform_metrics && (
                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                  <div className="font-medium text-gray-700 mb-1">Reach Metrics:</div>
                                  <div className="grid grid-cols-2 gap-1">
                                    {show.platform_metrics.total_followers > 0 && (
                                      <span className="text-gray-600">
                                        👥 {(show.platform_metrics.total_followers / 1000).toFixed(1)}K followers
                                      </span>
                                    )}
                                    {show.platform_metrics.total_views > 0 && (
                                      <span className="text-gray-600">
                                        👁️ {(show.platform_metrics.total_views / 1000).toFixed(1)}K views
                                      </span>
                                    )}
                                    {show.platform_metrics.total_subscribers > 0 && (
                                      <span className="text-gray-600">
                                        📺 {(show.platform_metrics.total_subscribers / 1000).toFixed(1)}K subscribers
                                      </span>
                                    )}
                                    {show.platform_metrics.platform_breakdown.imdb.rating > 0 && (
                                      <span className="text-gray-600">
                                        ⭐ {show.platform_metrics.platform_breakdown.imdb.rating.toFixed(1)} IMDB
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Platform breakdown */}
                                  <div className="flex gap-2 mt-1">
                                    {show.platform_metrics.platform_breakdown.tiktok.followers > 0 && (
                                      <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                                        TikTok: {(show.platform_metrics.platform_breakdown.tiktok.followers / 1000).toFixed(1)}K
                                      </Badge>
                                    )}
                                    {show.platform_metrics.platform_breakdown.instagram.followers > 0 && (
                                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                        IG: {(show.platform_metrics.platform_breakdown.instagram.followers / 1000).toFixed(1)}K
                                      </Badge>
                                    )}
                                    {show.platform_metrics.platform_breakdown.youtube.subscribers > 0 && (
                                      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                        YT: {(show.platform_metrics.platform_breakdown.youtube.subscribers / 1000).toFixed(1)}K
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedShows(prev => ({
                                    ...prev,
                                    anchor: [...prev.anchor, show]
                                  }));
                                  setPackageFormErrors(prev => {
                                    if (!prev.anchor) return prev;
                                    const next = { ...prev };
                                    delete next.anchor;
                                    return next;
                                  });
                                }}
                                disabled={selectedShows.anchor.some(s => s.id === show.id) || selectedShows.fill.some(s => s.id === show.id)}
                              >
                                Anchor
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedShows(prev => ({
                                  ...prev,
                                  fill: [...prev.fill, show]
                                }))}
                                disabled={selectedShows.anchor.some(s => s.id === show.id) || selectedShows.fill.some(s => s.id === show.id)}
                              >
                                Fill
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Selected Shows</h4>
                    <div className="space-y-3">
                      {selectedShows.anchor.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-blue-600">Anchor Shows *</h5>
                          {selectedShows.anchor.map((show) => (
                            <div key={show.id} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                              <span className="text-sm">{show.show_name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedShows(prev => ({
                                  ...prev,
                                  anchor: prev.anchor.filter(s => s.id !== show.id)
                                }))}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {selectedShows.fill.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-green-600">Fill Shows</h5>
                          {selectedShows.fill.map((show) => (
                            <div key={show.id} className="flex justify-between items-center p-2 bg-green-50 rounded">
                              <span className="text-sm">{show.show_name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedShows(prev => ({
                                  ...prev,
                                  fill: prev.fill.filter(s => s.id !== show.id)
                                }))}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreatePackage} disabled={isCreatingPackage}>
                  {isCreatingPackage ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding package...
                    </>
                  ) : (
                    'Create Package'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Total Packages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{packages.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Active Packages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {packages.filter(p => p.status === 'active').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(packages.reduce((sum, p) => sum + p.total_cost, 0))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Reach
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(packages.reduce((sum, p) => sum + p.total_reach, 0))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Packages on Hold
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {packages.filter(p =>
                p.held_by_campaign_id != null ||
                (p as any).campaign_status === 'hold'
              ).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Packages Tabs */}
      {isRefreshingPackages && (
        <div className="inline-flex items-center text-xs text-gray-500">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Refreshing packages...
        </div>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="all">All Packages ({packages.length})</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Approval ({pendingPackages.length})
            {pendingPackages.length > 0 && (
              <Badge className="ml-2 bg-orange-100 text-orange-800 border-orange-200">
                {pendingPackages.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle>All Packages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package Name</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Shows</TableHead>
                      <TableHead>Total Cost</TableHead>
                      <TableHead>Total Reach</TableHead>
                      <TableHead>Slots</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{pkg.name}</div>
                            <div className="text-sm text-gray-600">{pkg.headline}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {pkg.brand_name ? (
                              <div className="font-medium text-blue-600">{pkg.brand_name}</div>
                            ) : pkg.brand_id ? (
                              <div className="font-medium text-blue-600">Brand ID: {pkg.brand_id}</div>
                            ) : (
                              <span className="text-gray-400">No brand assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {(pkg as any).campaign_name ? (
                              <div className="font-medium text-green-600">{(pkg as any).campaign_name}</div>
                            ) : (pkg as any).campaign_id ? (
                              <div className="font-medium text-green-600">Campaign ID: {(pkg as any).campaign_id}</div>
                            ) : (
                              <span className="text-gray-400">No campaign assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(pkg.held_by_campaign_id || (pkg as any).campaign_status === 'hold') ? (
                            <Badge className={statusColors.hold_placed}>
                              {statusIcons.hold_placed}
                              <span className="ml-1">ON HOLD</span>
                            </Badge>
                          ) : (
                            <Badge className={statusColors[pkg.status]}>
                              {statusIcons[pkg.status]}
                              <span className="ml-1">{pkg.status.toUpperCase()}</span>
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{((pkg.anchor_projects && pkg.anchor_projects.length > 0) ? pkg.anchor_projects : pkg.anchor_shows || []).length} anchor</div>
                            <div>{((pkg.fill_projects && pkg.fill_projects.length > 0) ? pkg.fill_projects : pkg.fill_shows || []).length} fill</div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(pkg.total_cost)}
                        </TableCell>
                        <TableCell>
                          {formatNumber(pkg.total_reach)}
                        </TableCell>
                        <TableCell>
                          {pkg.total_slots}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {new Date(pkg.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewPackage(pkg)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyPackageRow(pkg)}
                              title="Copy package row"
                              aria-label="Copy package row"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDuplicatePackage(pkg.id)}
                              title="Duplicate package"
                              aria-label="Duplicate package"
                              disabled={duplicatingPackageId !== null}
                            >
                              {duplicatingPackageId === pkg.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Package className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeletePackage(pkg.id, pkg.name, pkg.status)}
                              disabled={deletingPackageId !== null}
                            >
                              {deletingPackageId === pkg.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                            <Select onValueChange={(value) => handleUpdatePackageStatus(pkg.id, value)}>
                              <SelectTrigger className="w-20 h-8">
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="paused">Paused</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="hold_placed">Hold Placed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {packages.length === 0 && (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No packages created yet</h3>
                  <p className="text-gray-600 mb-4">
                    Create your first package to start offering curated inventory to brands.
                  </p>
                  <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Package
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Packages Pending Approval</CardTitle>
              <p className="text-sm text-gray-600">
                Review and approve packages before they become available to brands
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package Name</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Shows</TableHead>
                      <TableHead>Total Cost</TableHead>
                      <TableHead>Total Reach</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPackages.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{pkg.name}</div>
                            <div className="text-sm text-gray-600">{pkg.headline}</div>
                            {pkg.brand_campaign_id && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                Auto-generated
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {pkg.brand_name ? (
                              <div className="font-medium text-blue-600">{pkg.brand_name}</div>
                            ) : pkg.brand_id ? (
                              <div className="font-medium text-blue-600">Brand ID: {pkg.brand_id}</div>
                            ) : (
                              <span className="text-gray-400">No brand assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {(pkg as any).campaign_name ? (
                              <div className="font-medium text-green-600">{(pkg as any).campaign_name}</div>
                            ) : (pkg as any).campaign_id ? (
                              <div className="font-medium text-green-600">Campaign ID: {(pkg as any).campaign_id}</div>
                            ) : (
                              <span className="text-gray-400">No campaign assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                            <Clock className="h-3 w-3 mr-1" />
                            PENDING
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{((pkg.anchor_projects && pkg.anchor_projects.length > 0) ? pkg.anchor_projects : pkg.anchor_shows || []).length} anchor</div>
                            <div>{((pkg.fill_projects && pkg.fill_projects.length > 0) ? pkg.fill_projects : pkg.fill_shows || []).length} fill</div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(pkg.total_cost)}
                        </TableCell>
                        <TableCell>
                          {formatNumber(pkg.total_reach)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {new Date(pkg.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewPackage(pkg)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprovePackage(pkg.id)}
                              disabled={approvingPackageId !== null}
                            >
                              {approvingPackageId === pkg.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleRejectPackage(pkg.id)}
                              disabled={rejectingPackageId !== null}
                            >
                              {rejectingPackageId === pkg.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  Rejecting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Reject
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeletePackage(pkg.id, pkg.name, pkg.status)}
                              disabled={deletingPackageId !== null}
                            >
                              {deletingPackageId === pkg.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {pendingPackages.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No packages pending approval</h3>
                  <p className="text-gray-600">
                    All packages have been reviewed. New packages will appear here when created.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Package Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Package Details
            </DialogTitle>
          </DialogHeader>
          
          {viewingPackage && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-sm text-gray-600 mb-2">Package Name</h3>
                  <p className="font-medium">{viewingPackage.name}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-gray-600 mb-2">Status</h3>
                  {(viewingPackage.held_by_campaign_id || (viewingPackage as any).campaign_status === 'hold') ? (
                    <Badge className={statusColors.hold_placed}>
                      {statusIcons.hold_placed}
                      <span className="ml-1">ON HOLD</span>
                    </Badge>
                  ) : (
                    <Badge className={statusColors[viewingPackage.status]}>
                      {statusIcons[viewingPackage.status]}
                      <span className="ml-1">{viewingPackage.status.toUpperCase()}</span>
                    </Badge>
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-sm text-gray-600 mb-2">Headline</h3>
                  <p>{viewingPackage.headline}</p>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-gray-600 mb-2">Created</h3>
                  <p>{new Date(viewingPackage.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-medium text-sm text-gray-600 mb-2">Description</h3>
                <p className="text-sm text-gray-700">{viewingPackage.description}</p>
              </div>

              {/* Package Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-sm text-blue-600 mb-1">Total Cost</h3>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(viewingPackage.total_cost)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-medium text-sm text-purple-600 mb-1">Total Slots</h3>
                  <p className="text-2xl font-bold text-purple-700">{viewingPackage.total_slots}</p>
                </div>
              </div>

              {/* Platform Reach Metrics */}
              {viewingPackageReachMetrics && (
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-medium text-sm text-indigo-600 mb-1">Total Reach</h3>
                  <p className="text-2xl font-bold text-indigo-700">
                    {formatNumber(
                      viewingPackageReachMetrics.reach_metrics.total_followers +
                      viewingPackageReachMetrics.reach_metrics.total_views +
                      viewingPackageReachMetrics.reach_metrics.total_subscribers
                    )}
                  </p>
                </div>
              )}

              {/* Shows */}
              <div className="space-y-4">
                {(((viewingPackage.anchor_projects && viewingPackage.anchor_projects.length > 0) ? viewingPackage.anchor_projects : viewingPackage.anchor_shows || []).length > 0) && (
                  <div>
                    <h3 className="font-medium text-sm text-gray-600 mb-3">Anchor Shows ({((viewingPackage.anchor_projects && viewingPackage.anchor_projects.length > 0) ? viewingPackage.anchor_projects : viewingPackage.anchor_shows || []).length})</h3>
                    <div className="space-y-2">
                      {((viewingPackage.anchor_projects && viewingPackage.anchor_projects.length > 0) ? viewingPackage.anchor_projects : viewingPackage.anchor_shows || []).map((show, index) => {
                        console.log(`DEBUG: Anchor show ${index} slots:`, show.slots);
                        return (
                          <div key={index} className="border rounded-lg p-3 bg-blue-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{show.show_name || show.project_name}</h4>
                                <p className="text-sm text-gray-600">{show.creator} • {show.vertical}</p>
                                <p className="text-sm text-gray-600">Artie Score: {show.artie_score.toFixed(1)} • Reach: {formatNumber(
                                  ((show as any).platform_metrics?.total_followers || 0) +
                                  ((show as any).platform_metrics?.total_views || 0) +
                                  ((show as any).platform_metrics?.total_subscribers || 0)
                                )}</p>
                              </div>
                              <Badge variant="outline" className="bg-blue-100 text-blue-800">
                                {show.slots?.length || 0} slots
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(((viewingPackage.fill_projects && viewingPackage.fill_projects.length > 0) ? viewingPackage.fill_projects : viewingPackage.fill_shows || []).length > 0) && (
                  <div>
                    <h3 className="font-medium text-sm text-gray-600 mb-3">Fill Shows ({((viewingPackage.fill_projects && viewingPackage.fill_projects.length > 0) ? viewingPackage.fill_projects : viewingPackage.fill_shows || []).length})</h3>
                    <div className="space-y-2">
                      {((viewingPackage.fill_projects && viewingPackage.fill_projects.length > 0) ? viewingPackage.fill_projects : viewingPackage.fill_shows || []).map((show, index) => {
                        console.log(`DEBUG: Fill show ${index} slots:`, show.slots);
                        return (
                          <div key={index} className="border rounded-lg p-3 bg-green-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{show.show_name || show.project_name}</h4>
                                <p className="text-sm text-gray-600">{show.creator} • {show.vertical}</p>
                                <p className="text-sm text-gray-600">Artie Score: {show.artie_score.toFixed(1)} • Reach: {formatNumber(
                                  ((show as any).platform_metrics?.total_followers || 0) +
                                  ((show as any).platform_metrics?.total_views || 0) +
                                  ((show as any).platform_metrics?.total_subscribers || 0)
                                )}</p>
                              </div>
                              <Badge variant="outline" className="bg-green-100 text-green-800">
                                {show.slots?.length || 0} slots
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Targeting */}
              <div>
                <h3 className="font-medium text-sm text-gray-600 mb-3">Targeting</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-xs text-gray-500 mb-2">Target Demographics</h4>
                    <div className="flex flex-wrap gap-1">
                      {viewingPackage.targeting?.target_demographics?.length > 0 ? (
                        viewingPackage.targeting.target_demographics.map((demo, index) => (
                          <Badge key={index} variant="outline" className="text-xs">{demo}</Badge>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">No demographics specified</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-xs text-gray-500 mb-2">Target Categories</h4>
                    <div className="flex flex-wrap gap-1">
                      {viewingPackage.targeting?.target_categories?.length > 0 ? (
                        viewingPackage.targeting.target_categories.map((category, index) => (
                          <Badge key={index} variant="outline" className="text-xs">{category}</Badge>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">No categories specified</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-xs text-gray-500 mb-2">Budget Range</h4>
                    <p className="text-sm">{formatCurrency(viewingPackage.targeting?.min_budget || 0)} - {formatCurrency(viewingPackage.targeting?.max_budget || 0)}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-xs text-gray-500 mb-2">eCPM Range</h4>
                    <p className="text-sm">
                      ${viewingPackage.targeting?.target_ecpm_range?.min || 0} - ${viewingPackage.targeting?.target_ecpm_range?.max || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Fit Rationale */}
              {viewingPackage.fit_rationale && (
                <div>
                  <h3 className="font-medium text-sm text-gray-600 mb-2">Fit Rationale</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingPackage.fit_rationale}</p>
                </div>
              )}

              {/* Auto-generated indicator */}
              {viewingPackage.brand_campaign_id && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800">Auto-generated Package</span>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    This package was automatically created based on slot assignments for brand campaign ID: {viewingPackage.brand_campaign_id}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackageManagementPage;